import type { ComponentProps } from 'react';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui';
import { LocationPinIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { ComboBox } from './components/ComboBox';
import { useEditLocationModal } from './hooks/useEditLocationModal';

type EditLocationModalProps = Required<Pick<ComponentProps<typeof Modal>, 'trigger' | 'isOpen' | 'onClose'>>;

export const EditLocationModal: React.FC<EditLocationModalProps> = ({ trigger, isOpen, onClose }) => {
	const { translate } = useI18n();
	const { data, actions } = useEditLocationModal({ closeModal: onClose });

	return (
		<Modal
			onClose={actions.handleModalClose}
			isOpen={isOpen}
			trigger={trigger}
			size="large"
			className="overflow-y-auto [&>div>div]:!max-h-[initial] [&>div>div]:flex-[1_0_auto] [&_section]:h-full"
		>
			<ModalHeader
				title={
					<Text>
						<Translate value="corgi.homepage.local_section.modal.header.title" />
					</Text>
				}
				subtitle={
					<Text>
						<Translate value="corgi.homepage.local_section.modal.header.subtitle" />
					</Text>
				}
			/>
			<ModalBody className="!max-h-[initial] flex-[1_0_auto] md:min-h-[318px]">
				<ComboBox
					id="petition-place"
					name="petition-place"
					label={translate('corgi.homepage.local_section.modal.body.search_input.label')}
					inputValue={data.placeInputValue}
					onInputChange={(_, newValue) => actions.handlePlaceInputChange(newValue)}
					value={data.placeValue}
					onChange={(_, newValue) => actions.handlePlaceChange(newValue)}
					options={data.places}
					filterOptions={() => data.places}
					optionToString={(place) => place.name}
					error={data.formError}
					renderOption={(option) => (
						<div className="flex items-center space-x-2">
							<LocationPinIcon className="text-content-tertiary-tertiary" />
							<span>{option.name}</span>
						</div>
					)}
					data-qa="edit-location-modal-combobox"
				/>
			</ModalBody>
			<ModalFooter>
				<Button variant="primary" onClick={actions.handleLocationSubmit} data-qa="edit-location-modal-primary-action">
					<Translate value="corgi.homepage.local_section.modal.footer.primary_action" />
				</Button>
			</ModalFooter>
		</Modal>
	);
};
